// =========================================================
// Vojjin STORAGE Utilities
// =========================================================

;(function (global) {
    // Create namespace
    const G = global.gemini || (global.gemini = {});
    G.storage = {};

    let storage = window.localStorage;

    /**
     * Retrieves a value from the storage system associated with the provided key.
     *
     * This method is used to fetch data that has been previously stored in the
     * application's storage layer. The key is used as an identifier to locate
     * the stored value. If the key does not exist, the method typically returns
     * undefined or null based on implementation.
     *
     * @param {string} key - The key associated with the value to retrieve from storage.
     * @returns {*} The value retrieved from storage, or null/undefined if the key does not exist.
     */
    G.storage.get = key => storage.getItem(key) ?? "";

    /**
     * Stores a value in the specified storage with an associated key.
     *
     * This function allows you to persist data in storage, associating it
     * with a unique key for later retrieval. Use this method for saving
     * application data that needs to be accessed or updated later.
     *
     * @function
     * @param {string} key - The key identifying the value to be stored.
     * @param {*} value - The value to store in the storage. Can be of any type.
     * @throws {Error} Throws an error if the key is invalid or storage operation fails.
     * @example
     * // This function call stores the given value under the specified key.
     * G.storage.set('username', 'JohnDoe');
     */
    G.storage.set = (key, value) => {
        storage.setItem(key, value ?? "");
    };

    /**
     * Removes a key-value pair from storage.
     *
     * This function deletes the specified key from the storage, freeing up space
     * and ensuring that the associated value is no longer accessible.
     *
     * @function
     * @name G.storage.remove
     * @param {string} key - The key of the item to remove from storage.
     */
    G.storage.remove = key => {
        const raw = storage.getItem(key);
        if (!raw) return;
        storage.removeItem(key);
    }

    /**
     * Retrieves a parsed JavaScript object from local storage by the given key.
     *
     * @function
     * @param {string} key - The key under which the object is stored in local storage.
     * @returns {Object|null} The parsed object if found and successfully parsed, or null if the key does not exist or the stored value is not a valid JSON string.
     */
    G.storage.getObject = key => {
        const raw = storage.getItem(key);
        if (!raw) return {};
        try {
            return JSON.parse(raw);
        } catch {
            return {};
        }
    };

    /**
     * Stores an object in the storage system under the specified key.
     * The object will be serialized to a JSON string before storage.
     *
     * @function
     * @param {string} key - The unique identifier under which the object should be stored.
     * @param {Object} value - The object to be stored. Must be serializable to JSON.
     * @throws {TypeError} Throws an error if the key is not a string or if the value cannot be serialized.
     */
    G.storage.setObject = (key, value) => {
        storage.setItem(key, JSON.stringify(value));
    };

    /**
     * Retrieves the value of a specified key from storage as a parsed JavaScript object.
     *
     * This method fetches data from the storage and parses it as JSON before returning.
     * If the key does not exist or the data is not a valid JSON string, it will
     * return null or an appropriate default value.
     *
     * Use this method when you expect the stored data to be in JSON format and want it
     * parsed into a JavaScript object for further manipulation.
     *
     * @param {string} key - The key associated with the stored data to be retrieved.
     * @param {string} id - The property name of the object to retrieve from the parsed JSON data.
     * @param {string} defaultValue - Default value to return if the key does not exist or the stored data is not a valid JSON string. Defaults to null
     * @returns {Object|null} The parsed object value associated with the key, or null if unavailable or invalid.
     */
    G.storage.getObjectValue = (key, id, defaultValue) => {
        const obj = G.storage.getObject(key);
        return Object.prototype.hasOwnProperty.call(obj, id)
            ? obj[id]
            : defaultValue;
    };


    if (typeof module !== "undefined" && module.exports) {
        module.exports = G.storage;
    } else {
        global.gemini = G;
        global.gemini.storage = G.storage;

    }
})(typeof globalThis !== "undefined" ? globalThis : this);
